import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import PriorityBadge from "./PriorityBadge";
import SuccessSubmit from "./SuccessSubmit";
import * as FiIcons from "react-icons/fi";
import SafeIcon from "../common/SafeIcon";

const { FiX, FiInfo } = FiIcons;

const inputClass =
  "w-full px-3 py-2.5 rounded-lg text-sm text-white placeholder-gray-500 outline-none transition-all focus:ring-1 focus:ring-violet-500";
const inputStyle = {
  background: "rgba(255,255,255,0.06)",
  border: "1px solid rgba(255,255,255,0.10)",
};
const errorInputStyle = {
  background: "rgba(255,255,255,0.06)",
  border: "1px solid rgba(239,68,68,0.45)",
};
const labelClass = "text-xs font-medium text-gray-400 block mb-1.5";

const JUSTIFICATION_MIN_LENGTH = 50;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const initialState = {
  feature: "",
  description: "",
  useCase: "",
  discordUsername: "",
  email: "",
  category: "",
  priority: "Medium",
  justification: "",
  status: "Requested",
};

const SuggestFeatureModal = ({ isOpen, onClose, onSubmit, isAdmin = false }) => {
  const [form, setForm] = useState(initialState);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [showSuccess, setShowSuccess] = useState(false);

  const needsJustification = form.priority === "High" || form.priority === "Urgent";

  const updateField = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    setFieldErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const validate = () => {
    const errs = {};
    if (!form.feature.trim()) errs.feature = "Feature name is required.";
    if (!form.description.trim()) errs.description = "Description is required.";
    // Admins adding roadmap items directly (e.g. an already-built feature)
    // aren't "requesters", so use case / Discord / email aren't required.
    if (!isAdmin) {
      if (!form.useCase.trim()) errs.useCase = "Please tell us what you're trying to accomplish.";
      if (!form.discordUsername.trim()) errs.discordUsername = "Discord username is required.";
      if (!form.email.trim()) {
        errs.email = "Email address is required.";
      } else if (!EMAIL_REGEX.test(form.email.trim())) {
        errs.email = "Please enter a valid email address.";
      }
    } else if (form.email.trim() && !EMAIL_REGEX.test(form.email.trim())) {
      errs.email = "Please enter a valid email address.";
    }
    if (needsJustification && form.justification.trim().length < JUSTIFICATION_MIN_LENGTH) {
      errs.justification = `Please explain why this is ${form.priority.toLowerCase()} priority (at least ${JUSTIFICATION_MIN_LENGTH} characters, ${Math.max(
        0,
        JUSTIFICATION_MIN_LENGTH - form.justification.trim().length
      )} more needed).`;
    }
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs);
      setError("Please fix the highlighted fields before submitting.");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const featureData = {
        feature: form.feature.trim(),
        description: form.description.trim(),
        use_case: form.useCase.trim() || null,
        discord_username: form.discordUsername.trim() || null,
        email: form.email.trim() || null,
        category: form.category.trim() || null,
        priority: form.priority,
        justification: needsJustification ? form.justification.trim() : null,
        ...(isAdmin ? { status: form.status } : {}),
      };
      if (onSubmit) await onSubmit(featureData);
      setForm(initialState);
      if (isAdmin) {
        onClose();
      } else {
        setShowSuccess(true);
      }
    } catch (err) {
      console.error("Error submitting feature request:", err);
      setError("Failed to submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setForm(initialState);
    setError("");
    setFieldErrors({});
    setShowSuccess(false);
    onClose();
  };

  const isValidEnough =
    form.feature.trim() &&
    form.description.trim() &&
    (isAdmin || (form.useCase.trim() && form.discordUsername.trim() && form.email.trim())) &&
    (!needsJustification || form.justification.trim().length >= JUSTIFICATION_MIN_LENGTH);

  return (
    <>
      <AnimatePresence>
        {isOpen && !showSuccess && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0"
              style={{ background: "rgba(0,0,0,0.75)", backdropFilter: "blur(4px)" }}
              onClick={handleClose}
            />

            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative w-full max-w-md max-h-[90vh] overflow-y-auto rounded-2xl"
              style={{
                background: "#13131c",
                border: "1px solid rgba(255,255,255,0.10)",
                boxShadow: "0 24px 80px rgba(0,0,0,0.7), 0 0 60px rgba(124,58,237,0.10)",
              }}
            >
              {/* Header */}
              <div
                className="flex items-center justify-between px-6 pt-6 pb-4"
                style={{ borderBottom: "1px solid rgba(255,255,255,0.07)" }}
              >
                <div>
                  <h2 className="text-lg font-semibold text-white">
                    {isAdmin ? "Add Feature to Roadmap" : "Request a Feature"}
                  </h2>
                  <p className="text-sm text-gray-500 mt-0.5">
                    {isAdmin
                      ? "Add a roadmap item directly — requester fields are optional."
                      : "Share what you'd like to see in Greta."}
                  </p>
                </div>
                <button
                  onClick={handleClose}
                  className="p-1.5 text-gray-500 hover:text-white transition-colors rounded-lg hover:bg-white/5"
                >
                  <SafeIcon icon={FiX} className="w-4 h-4" />
                </button>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="p-6 flex flex-col gap-4">
                {/* Feature name */}
                <div>
                  <label className={labelClass}>Feature name *</label>
                  <input
                    type="text"
                    value={form.feature}
                    onChange={updateField("feature")}
                    placeholder="e.g. Dark mode support"
                    className={inputClass}
                    style={fieldErrors.feature ? errorInputStyle : inputStyle}
                  />
                  {fieldErrors.feature && <p className="text-xs text-red-400 mt-1">{fieldErrors.feature}</p>}
                </div>

                {/* Description */}
                <div>
                  <label className={labelClass}>Description *</label>
                  <textarea
                    value={form.description}
                    onChange={updateField("description")}
                    placeholder="Describe what the feature should do..."
                    rows={3}
                    className={inputClass + " resize-none"}
                    style={fieldErrors.description ? errorInputStyle : inputStyle}
                  />
                  {fieldErrors.description && <p className="text-xs text-red-400 mt-1">{fieldErrors.description}</p>}
                </div>

                {/* Use case */}
                <div>
                  <label className={labelClass}>
                    Use case — why do you need this?{isAdmin ? " " : " *"}
                    {isAdmin && <span className="text-gray-600">(optional)</span>}
                  </label>
                  <textarea
                    value={form.useCase}
                    onChange={updateField("useCase")}
                    placeholder="What are you trying to accomplish? What problem does this solve for you?"
                    rows={3}
                    className={inputClass + " resize-none"}
                    style={fieldErrors.useCase ? errorInputStyle : inputStyle}
                  />
                  {fieldErrors.useCase && <p className="text-xs text-red-400 mt-1">{fieldErrors.useCase}</p>}
                </div>

                {/* Discord + Email row */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className={labelClass}>
                      Discord username{isAdmin ? " " : " *"}
                      {isAdmin && <span className="text-gray-600">(optional)</span>}
                    </label>
                    <input
                      type="text"
                      value={form.discordUsername}
                      onChange={updateField("discordUsername")}
                      placeholder="e.g. yourname"
                      className={inputClass}
                      style={fieldErrors.discordUsername ? errorInputStyle : inputStyle}
                    />
                    {fieldErrors.discordUsername ? (
                      <p className="text-xs text-red-400 mt-1">{fieldErrors.discordUsername}</p>
                    ) : (
                      <p className="text-[11px] text-gray-600 mt-1">Won't be disclosed publicly.</p>
                    )}
                  </div>
                  <div>
                    <label className={labelClass}>
                      Email address{isAdmin ? " " : " *"}
                      {isAdmin && <span className="text-gray-600">(optional)</span>}
                    </label>
                    <input
                      type="email"
                      value={form.email}
                      onChange={updateField("email")}
                      placeholder="you@example.com"
                      className={inputClass}
                      style={fieldErrors.email ? errorInputStyle : inputStyle}
                    />
                    {fieldErrors.email ? (
                      <p className="text-xs text-red-400 mt-1">{fieldErrors.email}</p>
                    ) : (
                      <p className="text-[11px] text-gray-600 mt-1">Won't be disclosed publicly.</p>
                    )}
                  </div>
                </div>

                {/* Status (admin only) */}
                {isAdmin && (
                  <div>
                    <label className={labelClass}>Status</label>
                    <select
                      value={form.status}
                      onChange={updateField("status")}
                      className={inputClass}
                      style={inputStyle}
                    >
                      <option value="Requested">Requested</option>
                      <option value="Planned">Planned</option>
                      <option value="In Progress">In Progress</option>
                      <option value="In Beta">In Beta</option>
                      <option value="Released">Released</option>
                    </select>
                  </div>
                )}

                {/* Category */}
                <div>
                  <label className={labelClass}>Category <span className="text-gray-600">(optional)</span></label>
                  <input
                    type="text"
                    value={form.category}
                    onChange={updateField("category")}
                    placeholder="e.g. Integration, UI, Backend..."
                    className={inputClass}
                    style={inputStyle}
                  />
                </div>

                {/* Priority */}
                <div>
                  <label className={labelClass}>Priority</label>
                  <select
                    value={form.priority}
                    onChange={updateField("priority")}
                    className={inputClass}
                    style={inputStyle}
                  >
                    <option value="Urgent">Urgent</option>
                    <option value="High">High</option>
                    <option value="Medium">Medium</option>
                    <option value="Low">Low</option>
                  </select>
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-xs text-gray-600">Preview:</span>
                    <PriorityBadge priority={form.priority} />
                  </div>
                </div>

                {/* High/Urgent justification */}
                <AnimatePresence>
                  {needsJustification && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      className="overflow-hidden"
                    >
                      <div
                        className="p-3 rounded-lg mb-2 flex items-start gap-2"
                        style={{ background: "rgba(234,179,8,0.08)", border: "1px solid rgba(234,179,8,0.25)" }}
                      >
                        <SafeIcon icon={FiInfo} className="w-4 h-4 text-yellow-400 shrink-0 mt-0.5" />
                        <p className="text-xs text-yellow-200/90 leading-relaxed">
                          Because you selected <strong>{form.priority}</strong> priority, we need a bit more
                          context so the team can properly prioritize your request against others.
                        </p>
                      </div>
                      <label className={labelClass}>Why is this request {form.priority}? *</label>
                      <textarea
                        value={form.justification}
                        onChange={updateField("justification")}
                        placeholder="Explain the impact, urgency, or business reason (min. 50 characters)..."
                        rows={3}
                        className={inputClass + " resize-none"}
                        style={fieldErrors.justification ? errorInputStyle : inputStyle}
                      />
                      <div className="flex items-center justify-between mt-1">
                        {fieldErrors.justification ? (
                          <p className="text-xs text-red-400">{fieldErrors.justification}</p>
                        ) : (
                          <span />
                        )}
                        <span
                          className={`text-xs ${
                            form.justification.trim().length >= JUSTIFICATION_MIN_LENGTH
                              ? "text-green-400"
                              : "text-gray-500"
                          }`}
                        >
                          {form.justification.trim().length >= JUSTIFICATION_MIN_LENGTH
                            ? "Minimum met ✓"
                            : `Minimum ${JUSTIFICATION_MIN_LENGTH} characters`}
                        </span>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Error */}
                {error && (
                  <div
                    className="px-3 py-2.5 rounded-lg text-sm text-red-300"
                    style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.20)" }}
                  >
                    {error}
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-3 pt-1">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="flex-1 py-2.5 rounded-xl text-sm font-medium text-gray-400 hover:text-white transition-colors"
                    style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)" }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading || !isValidEnough}
                    className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white transition-all active:scale-[0.97] disabled:opacity-40"
                    style={{ background: "linear-gradient(135deg,#7c3aed,#6d28d9)", boxShadow: "0 0 20px rgba(124,58,237,0.30)" }}
                  >
                    {loading ? "Submitting…" : "Submit Request"}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <SuccessSubmit isOpen={showSuccess} onClose={handleClose} />
    </>
  );
};

export default SuggestFeatureModal;
